import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Dialog,
  DialogContent,
  TextField,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Switch,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import promoCodeService from '../services/promoCodeService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const PromoCodes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form and dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    coupon_code: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    is_active: true,
    razorpay_offer_id: '',
  });

  // API state
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, runSave] = useActionLock();
  const [deleting, runDelete] = useActionLock();
  const [toggling, setToggling] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const hasFetched = useRef(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Helper function to format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper to convert yyyy-mm-dd to ISO datetime
  const toISODateTime = (dateString, isEndOfDay = false) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isEndOfDay) {
      date.setHours(23, 59, 59, 0);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    return date.toISOString();
  };

  // Helper to convert ISO datetime to yyyy-mm-dd for input
  const toDateInputValue = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  // Format Razorpay sync status
  const formatRazorpayStatus = (promo) => {
    if (promo.razorpay_offer_id) {
      return { linked: true, id: promo.razorpay_offer_id };
    }
    return { linked: false, id: null };
  };

  // Fetch promo codes function with pagination
  const fetchPromoCodes = async (currentPage = page, currentPageSize = pageSize) => {
    setLoading(true);
    setError('');

    const result = await promoCodeService.getPromoCodes({
      page: currentPage + 1,
      page_size: currentPageSize,
    });

    if (result.success) {
      setPromoCodes(result.data.results || result.data || []);
      setTotalCount(result.data.count || 0);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Fetch promo codes on component mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchPromoCodes();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const refreshPromoCodes = async () => {
    const result = await promoCodeService.getPromoCodes({
      page: page + 1,
      page_size: pageSize,
    });
    if (result.success) {
      setPromoCodes(result.data.results || result.data || []);
      setTotalCount(result.data.count || 0);
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPromoCodes(newPage, pageSize);
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0);
    fetchPromoCodes(0, newPageSize);
  };

  const getDefaultFormData = () => ({
    coupon_code: '',
    max_uses: '',
    valid_from: '',
    valid_until: '',
    is_active: true,
    razorpay_offer_id: '',
  });

  const handleAddNew = () => {
    setFormData(getDefaultFormData());
    setEditingPromo(null);
    setOpenDialog(true);
  };

  const handleEdit = (promo) => {
    setFormData({
      coupon_code: promo.coupon_code,
      max_uses: promo.max_uses || '',
      valid_from: toDateInputValue(promo.valid_from),
      valid_until: toDateInputValue(promo.valid_until),
      is_active: promo.is_active,
      razorpay_offer_id: promo.razorpay_offer_id || '',
    });
    setEditingPromo(promo);
    setOpenDialog(true);
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    // Validate coupon code
    if (!formData.coupon_code.trim()) {
      showSnackbar('Please enter a coupon code', 'error');
      return;
    }

    // Validate code format (uppercase, numbers, hyphens, underscores)
    const codeRegex = /^[A-Z0-9_-]+$/;
    if (!codeRegex.test(formData.coupon_code.trim())) {
      showSnackbar('Coupon code must contain only uppercase letters, numbers, hyphens, and underscores', 'error');
      return;
    }

    // Validate Razorpay offer ID format if provided
    if (formData.razorpay_offer_id && formData.razorpay_offer_id.trim()) {
      const offerIdRegex = /^offer_[a-zA-Z0-9]+$/;
      if (!offerIdRegex.test(formData.razorpay_offer_id.trim())) {
        showSnackbar('Invalid Razorpay Offer ID format. Should be like: offer_JHD834hjbxzhd38d', 'error');
        return;
      }
    }

    try {
      const payload = {
        coupon_code: formData.coupon_code.trim().toUpperCase(),
        is_active: formData.is_active,
        applicable_plan_ids: Array.isArray(formData.applicable_plan_ids) ? formData.applicable_plan_ids : [],
      };

      // Only include razorpay_offer_id if provided
      if (formData.razorpay_offer_id && formData.razorpay_offer_id.trim()) {
        payload.razorpay_offer_id = formData.razorpay_offer_id.trim();
      }

      let result;
      if (editingPromo) {
        const promoId = editingPromo.coupon_id;
        result = await promoCodeService.updatePromoCode(promoId, payload);
      } else {
        result = await promoCodeService.createPromoCode(payload);
      }

      if (result.success) {
        showSnackbar(editingPromo ? 'Coupon code updated successfully' : 'Coupon code created successfully');
        await refreshPromoCodes();
        setOpenDialog(false);
        setEditingPromo(null);
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while saving', 'error');
    }
  };

  const handleDelete = (promo) => {
    setPromoToDelete(promo);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const promoId = promoToDelete.coupon_id;
      const result = await promoCodeService.deletePromoCode(promoId);

      if (result.success) {
        showSnackbar('Coupon code deleted successfully');
        await refreshPromoCodes();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while deleting', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setPromoToDelete(null);
    }
  };

  const handleToggleActive = async (promo) => {
    const promoId = promo.coupon_id;
    setToggling(promoId);

    try {
      const result = await promoCodeService.toggleActive(promoId);

      if (result.success) {
        showSnackbar(`Coupon code ${result.data.is_active ? 'activated' : 'deactivated'} successfully`);
        await refreshPromoCodes();
      } else {
        showSnackbar(result.error, 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while toggling status', 'error');
    } finally {
      setToggling(null);
    }
  };

  const handleExport = () => {
    console.log('Exporting to Excel...');
  };

  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, minHeight: '100vh' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header with Buttons */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: 'white' }}
          >
            Manage Coupon Codes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleAddNew}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Add New Coupon
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Export as Excel
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} sx={{ color: '#4E83BD' }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      {!loading && !error && !isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid #ECF0F1',
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                  }}
                >
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '14px',
                      padding: '16px',
                    }}
                  >
                    Coupon Code
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '14px',
                      padding: '16px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Razorpay Offer ID
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '14px',
                      padding: '16px',
                    }}
                  >
                    Active
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '14px',
                      padding: '16px',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promoCodes.map((promo, index) => (
                  <TableRow
                    key={promo.coupon_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: '#ECF0F1',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        padding: '16px',
                        fontWeight: 600,
                      }}
                    >
                      {promo.coupon_code}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        padding: '16px',
                      }}
                    >
                      {promo.razorpay_offer_id ? (
                        <Tooltip title={promo.razorpay_offer_id}>
                          <Typography component="span" sx={{ fontSize: '13px', color: '#4E83BD', fontWeight: 500 }}>
                            {promo.razorpay_offer_id}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography component="span" sx={{ fontSize: '13px', color: '#94a3b8' }}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ padding: '16px' }}>
                      <Switch
                        checked={promo.is_active}
                        onChange={() => handleToggleActive(promo)}
                        disabled={toggling === promo.coupon_id}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4E83BD',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4E83BD',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: '16px' }}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(promo)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(promo)}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {promoCodes.length === 0 && !loading && (
            <Box sx={{ padding: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No Coupon Codes Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first coupon code
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': {
                    backgroundColor: '#3A6A99',
                  },
                }}
              >
                Add New Coupon
              </Button>
            </Box>
          )}

          {/* Pagination */}
          {promoCodes.length > 0 && (
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid #ECF0F1',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: '#64748B',
                  fontSize: '13px',
                },
                '.MuiTablePagination-select': {
                  color: '#2C3E50',
                },
                '.MuiTablePagination-actions': {
                  color: '#2C3E50',
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* Mobile Card View */}
      {!loading && !error && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {promoCodes.map((promo) => (
            <Paper
              key={promo.coupon_id}
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #ECF0F1',
                backgroundColor: '#FFFFFF',
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  backgroundColor: '#4E83BD',
                  padding: '10px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>
                  {promo.coupon_code}
                </Typography>
              </Box>

              {/* Card Content */}
              <Box sx={{ padding: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748B',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 0.5,
                  }}
                >
                  Coupon Code
                </Typography>
                <Typography
                  sx={{
                    color: '#2C3E50',
                    fontSize: '16px',
                    fontWeight: 700,
                    marginBottom: 1.5,
                  }}
                >
                  {promo.coupon_code}
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                      Razorpay Offer ID
                    </Typography>
                    <Typography sx={{ color: '#2C3E50', fontSize: '13px', fontWeight: 500 }}>
                      {promo.razorpay_offer_id || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: '#64748B', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 0.5 }}>
                      Active
                    </Typography>
                    <Chip
                      label={promo.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        backgroundColor: promo.is_active ? '#DBEAFE' : '#F3F4F6',
                        color: promo.is_active ? '#1D4ED8' : '#6B7280',
                        fontWeight: 600,
                        fontSize: '11px',
                        height: '22px',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Card Actions */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: '8px 16px',
                  borderTop: '1px solid #ECF0F1',
                  backgroundColor: '#F8FAFC',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '12px', color: '#64748B' }}>Active</Typography>
                  <Switch
                    checked={promo.is_active}
                    onChange={() => handleToggleActive(promo)}
                    disabled={toggling === promo.coupon_id}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4E83BD' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4E83BD' },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(promo)}
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': { backgroundColor: 'rgba(78, 131, 189, 0.2)' },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(promo)}
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': { backgroundColor: 'rgba(78, 131, 189, 0.2)' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}

          {/* Empty State for Mobile */}
          {promoCodes.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                padding: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1 }}>
                No Coupon Codes Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                Get started by creating your first coupon code
              </Typography>
              <Button
                variant="contained"
                onClick={handleAddNew}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#4E83BD',
                  '&:hover': { backgroundColor: '#3A6A99' },
                }}
              >
                Add New Coupon
              </Button>
            </Paper>
          )}

          {/* Pagination for Mobile */}
          {promoCodes.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                overflow: 'hidden',
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    padding: '8px',
                  },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    color: '#64748B',
                    fontSize: '12px',
                  },
                  '.MuiTablePagination-select': {
                    color: '#2C3E50',
                  },
                  '.MuiTablePagination-actions': {
                    color: '#2C3E50',
                  },
                }}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Add/Edit Coupon Code Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: '20px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '18px' }}>
            {editingPromo ? 'Edit Coupon Code' : 'Add New Coupon Code'}
          </Typography>
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left Section - Basic Details */}
            <Box sx={{
              flex: 1,
              padding: 3,
              borderRight: { md: '1px solid #E5E7EB' },
              borderBottom: { xs: '1px solid #E5E7EB', md: 'none' },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: '#4E83BD', borderRadius: 1 }} />
                <Typography sx={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>
                  Coupon Details
                </Typography>
              </Box>

              {/* Coupon Code */}
              <Box sx={{ marginBottom: 2.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Coupon Code <span style={{ color: '#DC2626' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., WELCOME20"
                  value={formData.coupon_code}
                  onChange={(e) => handleFormChange('coupon_code', e.target.value.toUpperCase())}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#4E83BD' },
                      '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                    },
                    '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B' },
                  }}
                />
              </Box>

              {/* Razorpay Offer ID */}
              <Box sx={{ marginBottom: 2.5 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Razorpay Offer ID (Optional)
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., offer_JHD834hjbxzhd38d"
                  value={formData.razorpay_offer_id}
                  onChange={(e) => handleFormChange('razorpay_offer_id', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: '#F8FAFC',
                      '& fieldset': { borderColor: '#E5E7EB' },
                      '&:hover fieldset': { borderColor: '#4E83BD' },
                      '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
                    },
                    '& .MuiInputBase-input': { fontSize: '14px', color: '#1E293B' },
                  }}
                />
                <Typography sx={{ fontSize: '11px', color: '#64748B', marginTop: 0.5, lineHeight: 1.4 }}>
                  Create offer in Razorpay Dashboard → Subscriptions → Offers, then paste the offer_id here.
                </Typography>
              </Box>

              {/* Active Status */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#F8FAFC',
                padding: '12px 16px',
                borderRadius: '10px',
                border: '1px solid #E5E7EB',
              }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Active Status
                </Typography>
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleFormChange('is_active', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4E83BD',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4E83BD',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Right Section - Razorpay Info & Actions */}
            <Box sx={{
              flex: 1,
              padding: 3,
              backgroundColor: '#FFFFFF',
            }}>
              {/* Razorpay Info Box */}
              <Box sx={{
                padding: 2,
                backgroundColor: '#FFF7ED',
                borderRadius: '10px',
                border: '1px solid #FDBA74',
              }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#C2410C', marginBottom: 1 }}>
                  Razorpay Offer Setup
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#9A3412', lineHeight: 1.5 }}>
                  Discount configuration (type, amount, duration) is managed in Razorpay Dashboard:
                </Typography>
                <Box component="ol" sx={{ margin: '8px 0 0 0', paddingLeft: '16px', fontSize: '11px', color: '#9A3412', lineHeight: 1.6 }}>
                  <li>Go to Razorpay Dashboard → Subscriptions → Offers</li>
                  <li>Create New Offer with discount settings</li>
                  <li>Copy the generated offer_id</li>
                  <li>Paste it in the Razorpay Offer ID field above</li>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, marginTop: 3 }}>
                <ActionButton
                  variant="contained"
                  fullWidth
                  loading={saving}
                  onClick={() => runSave(handleSave)}
                  loadingText="Saving..."
                  sx={{
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                    fontWeight: 600,
                    padding: '14px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3A6A99 0%, #2D5480 100%)',
                      boxShadow: '0 6px 16px rgba(78, 131, 189, 0.4)',
                    },
                  }}
                >
                  {editingPromo ? 'Update Coupon' : 'Create Coupon'}
                </ActionButton>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setOpenDialog(false)}
                  disabled={saving}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    color: '#64748B',
                    borderColor: '#E5E7EB',
                    padding: '12px',
                    borderRadius: '10px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogContent sx={{ padding: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '18px',
              color: '#2C3E50',
              marginBottom: 2,
            }}
          >
            Confirm Delete
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#6B7280',
              lineHeight: 1.6,
              marginBottom: 3,
            }}
          >
            Are you sure you want to delete the coupon code <strong>"{promoToDelete?.coupon_code}"</strong>? This action cannot be undone.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                color: '#6B7280',
                borderColor: '#D1D5DB',
                paddingX: 3,
                '&:hover': {
                  borderColor: '#9CA3AF',
                  backgroundColor: '#F9FAFB',
                },
              }}
            >
              Cancel
            </Button>
            <ActionButton
              variant="contained"
              loading={deleting}
              onClick={() => runDelete(handleConfirmDelete)}
              loadingText="Deleting..."
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                backgroundColor: '#DC2626',
                color: 'white',
                paddingX: 3,
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#B91C1C',
                },
              }}
            >
              Delete
            </ActionButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PromoCodes;
